/**
 * Haptic Feedback Utility for Mobile
 *
 * Provides tactile feedback on supported mobile devices using the Vibration API.
 * Falls back gracefully on unsupported devices.
 */

// Check if vibration API is supported
const isVibrationSupported = (): boolean => {
  return typeof navigator !== 'undefined' && 'vibrate' in navigator;
};

// Vibration patterns (in milliseconds)
const PATTERNS = {
  // Light tap - for selections, toggles
  light: 10,

  // Medium tap - for confirmations, button presses
  medium: 25,

  // Strong tap - for important actions, errors
  strong: 50,

  // Success pattern - two quick taps
  success: [10, 50, 10],

  // Error pattern - three quick taps
  error: [50, 30, 50, 30, 50],

  // Warning pattern - one long tap
  warning: [100],

  // Notification pattern - attention-grabbing
  notification: [50, 100, 50],
} as const;

type HapticPattern = keyof typeof PATTERNS;

/**
 * Trigger haptic feedback
 * @param pattern - The type of haptic feedback to trigger
 * @returns boolean indicating if vibration was triggered
 */
export function haptic(pattern: HapticPattern = 'medium'): boolean {
  if (!isVibrationSupported()) {
    return false;
  }

  try {
    const vibrationPattern = PATTERNS[pattern];
    return navigator.vibrate(vibrationPattern);
  } catch (error) {
    console.warn('Haptic feedback failed:', error);
    return false;
  }
}

/**
 * Light haptic feedback - for selections, toggles
 */
export function hapticLight(): boolean {
  return haptic('light');
}

/**
 * Medium haptic feedback - for confirmations, button presses
 */
export function hapticMedium(): boolean {
  return haptic('medium');
}

/**
 * Strong haptic feedback - for important actions
 */
export function hapticStrong(): boolean {
  return haptic('strong');
}

/**
 * Success haptic feedback - two quick taps
 */
export function hapticSuccess(): boolean {
  return haptic('success');
}

/**
 * Error haptic feedback - three quick taps
 */
export function hapticError(): boolean {
  return haptic('error');
}

/**
 * Warning haptic feedback - one long tap
 */
export function hapticWarning(): boolean {
  return haptic('warning');
}

/**
 * Notification haptic feedback - attention-grabbing pattern
 */
export function hapticNotification(): boolean {
  return haptic('notification');
}

/**
 * Custom haptic feedback with specified duration or pattern
 * @param durationOrPattern - Duration in ms or array of on/off durations
 */
export function hapticCustom(durationOrPattern: number | number[]): boolean {
  if (!isVibrationSupported()) {
    return false;
  }

  try {
    return navigator.vibrate(durationOrPattern);
  } catch (error) {
    console.warn('Custom haptic feedback failed:', error);
    return false;
  }
}

/**
 * Stop any ongoing vibration
 */
export function hapticStop(): boolean {
  if (!isVibrationSupported()) {
    return false;
  }

  try {
    return navigator.vibrate(0);
  } catch (error) {
    return false;
  }
}

/**
 * Check if haptic feedback is available on this device
 */
export function isHapticAvailable(): boolean {
  return isVibrationSupported();
}

// Default export for convenience
export default {
  light: hapticLight,
  medium: hapticMedium,
  strong: hapticStrong,
  success: hapticSuccess,
  error: hapticError,
  warning: hapticWarning,
  notification: hapticNotification,
  custom: hapticCustom,
  stop: hapticStop,
  isAvailable: isHapticAvailable,
};
